import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { getUser, getVooruitgang } from "../../Localstorage/localstorage";
import moment from "moment";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import { AXIOS_GET, AXIOS_POST } from "../../server/api/crud";
import Swal from "sweetalert2";
import { vooruitgangTabs } from "../../function";
import { defaultThemeColors, readThemeColors } from "../../utils/waChartTheme";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend
);

const TOOLTIP_ID = "chartjs-tooltip-evolution-client";

function checking(metric, metricName) {
  const existingValues = metric[metricName] || [];
  const currentDate = moment().format("YYYY-MM-DD");
  const existingIndex = existingValues.findIndex(
    (entry) => entry.date === currentDate && entry.hasOwnProperty("created_at")
  );
  return existingIndex !== -1;
}

const EvolutionClient = () => {
  const user = getUser();
  const [metricData, setMetricData] = useState({
    slaap: [],
    energie: [],
    stress: [],
    cravings: [],
  });

  const tabs = useMemo(
    () => vooruitgangTabs(user, getVooruitgang()),
    [user?.id]
  );
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  const [selectedMetric, setSelectedMetric] = useState(() => tabs[0]);
  const [refresh, setRefresh] = useState(false);
  const [themeColors, setThemeColors] = useState(defaultThemeColors);

  useEffect(() => {
    const sync = () => setThemeColors(readThemeColors());
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "style"],
    });
    return () => observer.disconnect();
  }, []);

  const fetchVooruitgangData = useCallback(async (p_id) => {
    try {
      const response = await AXIOS_GET(`vooruitgang/uservooruitgang/${p_id}`);
      if (response.status === 201) {
        const dataVooruitgang = {
          slaap: response.data.slaapDatas?.map((item) => ({
            ...item,
            date: moment(item.date).format("YYYY-MM-DD"),
          })),
          energie: response.data.energieDatas?.map((item) => ({
            ...item,
            date: moment(item.date).format("YYYY-MM-DD"),
          })),
          stress: response.data.stressDatas?.map((item) => ({
            ...item,
            date: moment(item.date).format("YYYY-MM-DD"),
          })),
          cravings: response.data.cravingsDatas?.map((item) => ({
            ...item,
            date: moment(item.date).format("YYYY-MM-DD"),
          })),
        };

        setMetricData(dataVooruitgang);
        if (!dataVooruitgang.slaap?.length) {
          setSelectedMetric(tabsRef.current[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, []);

  useEffect(() => {
    if (user?.id) fetchVooruitgangData(user.id);
  }, [fetchVooruitgangData, user?.id, refresh]);

  const handleMetricChange = (metricName, value) => {
    setSelectedMetric((prevData) => ({ ...prevData, score: value }));
    setMetricData((prevData) => {
      const existingValues = prevData[metricName] || [];
      const currentDate = moment().format("YYYY-MM-DD");
      const existingIndex = existingValues.findIndex(
        (entry) => entry.date === currentDate
      );

      if (existingIndex !== -1) {
        const updatedValues = existingValues.map((entry, index) =>
          index === existingIndex ? { ...entry, score: value } : entry
        );
        return { ...prevData, [metricName]: updatedValues };
      }

      return {
        ...prevData,
        [metricName]: [...existingValues, { date: currentDate, score: value }],
      };
    });
  };

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      color: themeColors.tick,
      scales: {
        x: {
          ticks: { color: themeColors.tick, maxRotation: 45, minRotation: 0 },
          grid: { color: themeColors.grid },
          border: { color: themeColors.grid },
        },
        y: {
          ticks: { color: themeColors.tick },
          grid: { color: themeColors.grid },
          border: { color: themeColors.grid },
        },
      },
      plugins: {
        legend: {
          labels: {
            color: themeColors.legend,
            usePointStyle: true,
            pointStyle: "rectRounded",
          },
        },
        tooltip: {
          callbacks: {
            title: function (tooltipItems) {
              return `${tooltipItems[0].label}`;
            },
            label: function (tooltipItem) {
              const index = tooltipItem?.dataIndex;
              const rawData = metricData[selectedMetric.key][index];
              return `Score: ${tooltipItem?.raw} ${
                rawData?.opmerking ? " | Meer uitleg : " + rawData?.opmerking : ""
              }`;
            },
            footer: function (tooltipItems) {
              return `Extra Info: ${tooltipItems.length} points`;
            },
          },
          enabled: false,
          external: function (context) {
            let tooltipEl = document.getElementById(TOOLTIP_ID);
            if (!tooltipEl) {
              tooltipEl = document.createElement("div");
              tooltipEl.id = TOOLTIP_ID;
              tooltipEl.style.position = "absolute";
              tooltipEl.style.padding = "0px";
              tooltipEl.style.pointerEvents = "none";
              tooltipEl.style.transition = "all 0.2s ease";
              tooltipEl.style.zIndex = "50";
              document.body.appendChild(tooltipEl);
            }

            const tooltipModel = context?.tooltip;
            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = "0";
              return;
            }

            if (tooltipModel?.body) {
              const index = tooltipModel?.dataPoints[0]?.dataIndex;
              const rawData = metricData[selectedMetric.key][index];

              let innerHtml = `
              <div class="text-xs"><strong style="color:${themeColors.legend}">Datum:</strong> <span style="color:${themeColors.legend}">${tooltipModel?.title[0]}</span></div>
              <div class="text-xs flex items-center gap-x-1" style="margin-top:4px">
                <div style="width:10px;height:10px;border-radius:3px;background:${themeColors.line}"></div>
                <strong style="color:${themeColors.legend}">Score:</strong> <span style="color:${themeColors.legend}">${tooltipModel?.dataPoints[0].raw}</span>
              </div>`;

              if (rawData?.uitleg) {
                innerHtml += `
              <div class="text-xs mt-2" style="color:${themeColors.tick}"><strong style="color:${themeColors.legend}">Meer info:</strong></div>
              <div class="text-xs" style="color:${themeColors.legend}">${rawData?.uitleg}</div>`;
              }

              tooltipEl.innerHTML = sanitizeHtml(
                `<div class="max-w-56 rounded-lg border p-3 shadow-lg" style="background:${themeColors.tooltipBg};border-color:${themeColors.tooltipBorder}">` +
                  innerHtml +
                  "</div>"
              );
            }

            const canvas = context.chart.canvas;
            const position = canvas.getBoundingClientRect();
            tooltipEl.style.opacity = "1";
            tooltipEl.style.left =
              position.left + window.pageXOffset + tooltipModel.caretX - 90 + "px";
            tooltipEl.style.top =
              position.top + window.pageYOffset + tooltipModel.caretY + "px";
          },
        },
      },
    }),
    [metricData, selectedMetric, themeColors]
  );

  const lineData = useMemo(() => {
    const key = selectedMetric.key;
    const labelBase =
      key.charAt(0).toUpperCase() + key.slice(1);
    return {
      labels: metricData[key]?.map((item) => `Dag ${moment(item.date).format("L")}`),
      datasets: [
        {
          label: `${labelBase} niveau`,
          data: metricData[key]?.map((item) => item.score),
          borderColor: themeColors.line,
          backgroundColor: themeColors.fill,
          fill: true,
          tension: 0.35,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: themeColors.line,
          pointBorderColor: themeColors.tooltipBg,
          borderWidth: 2,
        },
      ],
    };
  }, [metricData, selectedMetric.key, themeColors]);

  const scaleHint =
    selectedMetric.key === "slaap"
      ? "0 = Slechte nachtrust, 10 = Geslapen als een roos"
      : selectedMetric.key === "energie"
        ? "0 = Geen energie, 10 = Zeer energiek"
        : selectedMetric.key === "stress"
          ? "0 = Geen stress, 10 = Extreme stress"
          : "0 = Goede verzadiging, 10 = Continue cravings";

  const Opslaan = async () => {
    const newobj = { ...selectedMetric };
    newobj.date = moment(selectedMetric.date).format("YYYY-MM-DD");
    const checkingVooruitBestaat = checking(metricData, selectedMetric.key);

    if (!checkingVooruitBestaat) {
      await AXIOS_POST(`vooruitgang/details`, newobj)
        .then((respons) => {
          if (respons.status == 200) {
            Swal.fire({
              position: "top-end",
              icon: "success",
              title: `${selectedMetric.naam} is opgeslagen`,
              showConfirmButton: false,
              timer: 2500,
            });
            setRefresh((r) => !r);
          }
        })
        .catch(() => {
          Swal.fire({
            position: "top-end",
            icon: "error",
            title: `Er is iets mis gelopen`,
            showConfirmButton: false,
            timer: 2500,
          });
        });
    } else {
      Swal.fire({
        position: "top-end",
        icon: "error",
        title: `Er bestaat al een Score voor deze categorie op die datum`,
        showConfirmButton: false,
        timer: 2500,
      });
    }
  };

  return (
    <div className="min-h-0 flex-1 bg-wa-app px-4 py-6 text-wa-text sm:px-6 lg:py-8 lg:px-10">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight text-wa-text sm:text-3xl">
            Vooruitgang
          </h1>
          <p className="max-w-2xl text-sm leading-relaxed text-wa-muted">
            Volg uw scores per domein. Kies een tab, noteer vandaag en sla op.
          </p>
        </header>

        <div
          className="flex gap-1 overflow-x-auto rounded-xl border border-wa-border bg-wa-panel2/40 p-1 shadow-inner [-ms-overflow-style:none] [scrollbar-width:none] sm:flex-wrap sm:overflow-visible [&::-webkit-scrollbar]:hidden"
          role="tablist"
          aria-label="Metriek"
        >
          {tabs.map((tab) => {
            const active = selectedMetric.key === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active}
                className={`whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-semibold transition-all sm:px-5 sm:text-[0.95rem] ${
                  active
                    ? "bg-wa-accent text-wa-onaccent shadow-md ring-1 ring-wa-accent/50"
                    : "text-wa-muted hover:bg-wa-hover hover:text-wa-text"
                }`}
                onClick={() => setSelectedMetric(tab)}
              >
                {tab.naam}
              </button>
            );
          })}
        </div>

        <div className="overflow-hidden rounded-2xl border border-wa-border bg-wa-panel shadow-[0_24px_64px_-40px_rgba(0,0,0,0.65)]">
          <div className="border-b border-wa-border bg-gradient-to-br from-wa-panel via-wa-panel2/60 to-wa-panel px-4 py-3 sm:px-6 sm:py-4">
            <p className="text-xs font-medium uppercase tracking-wide text-wa-muted">
              Evolutie
            </p>
            <p className="text-sm font-semibold text-wa-text">
              {tabs.find((t) => t.key === selectedMetric?.key)?.naam}
            </p>
          </div>
          <div className="p-4 sm:p-6">
            <div className="h-[min(22rem,55vh)] min-h-[240px] sm:h-96">
              <Line data={lineData} options={chartOptions} />
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-wa-border bg-wa-panel shadow-[0_24px_64px_-40px_rgba(0,0,0,0.5)]">
          <div className="grid lg:grid-cols-2 lg:divide-x lg:divide-wa-border">
            <div className="space-y-5 p-6 sm:p-8 lg:border-b-0 lg:border-b-transparent">
              <div>
                <h2 className="text-lg font-semibold text-wa-text">
                  {tabs.find((t) => t.key === selectedMetric?.key)?.naam}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-wa-muted">{scaleHint}</p>
              </div>

              <div className="flex flex-wrap gap-2">
                {Array.from({ length: 11 }, (_, index) => {
                  const selected = selectedMetric.score === index;
                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleMetricChange(selectedMetric.key, index)}
                      className={`flex h-10 min-w-[2.35rem] items-center justify-center rounded-lg border text-sm font-semibold transition-all sm:h-11 sm:min-w-[2.5rem] ${
                        selected
                          ? "scale-[1.02] border-wa-accent bg-wa-accent text-wa-onaccent shadow-md ring-1 ring-wa-accent/40"
                          : "border-wa-border bg-wa-panel2/80 text-wa-text hover:border-wa-accent/35 hover:bg-wa-hover"
                      }`}
                    >
                      {index}
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-col gap-4 border-t border-wa-border pt-5 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-wa-muted">
                  <span className="font-medium text-wa-text">Huidige waarde:</span>{" "}
                  <span className="tabular-nums text-wa-accent">
                    {selectedMetric?.score ?? 0}
                  </span>
                </p>
                <button
                  type="button"
                  onClick={Opslaan}
                  className="inline-flex w-full items-center justify-center rounded-xl bg-wa-accent px-5 py-3 text-sm font-semibold text-wa-onaccent shadow-sm transition hover:bg-wa-accentdeep focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-wa-accent sm:w-auto sm:py-2.5"
                >
                  Opslaan
                </button>
              </div>
            </div>

            <div className="space-y-3 border-t border-wa-border p-6 sm:p-8 lg:border-t-0">
              <h2 className="text-lg font-semibold text-wa-text">Aanvulling</h2>
              <p className="text-xs text-wa-muted">
                Optionele toelichting bij uw score (zichtbaar in de grafiek-tooltip).
              </p>
              <textarea
                rows={8}
                name="uitleg"
                value={selectedMetric?.uitleg ?? ""}
                onChange={(e) =>
                  setSelectedMetric((prev) => ({
                    ...prev,
                    uitleg: e.target.value,
                  }))
                }
                placeholder="Schrijf hier uw opmerkingen…"
                className="block min-h-[10rem] w-full resize-y rounded-xl border border-wa-border bg-wa-panel2 px-4 py-3 text-sm text-wa-text shadow-inner outline-none transition placeholder:text-wa-muted focus:border-wa-accent/50 focus:ring-2 focus:ring-wa-accent/20"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvolutionClient;
